import type { Period } from "./pricing";

// Base for the public geocoding data API (used in code examples + the landing
// "try it" box). The portal management API is same-origin under /api.
export const DATA_BASE = "https://geo.zynday.com/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function req<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "include",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    throw new ApiError(401, "Not authenticated");
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, data?.error || `Request failed (${res.status})`);
  }
  return data as T;
}

// ---- types ----
export interface Me {
  email: string;
  plan: { period: Period; status: string; nextInvoice: string | null };
}
export interface ApiKey {
  id: string;
  name: string;
  created: string;
  display: string; // masked by server; full key only returned on create/rotate
  domains: string[];
}
export interface CreatedKey extends ApiKey {
  key: string; // plaintext, shown once
}
export interface Usage {
  bars: number[]; // last 14 days
  total: number;
  peak: number;
}

// ---- auth ----
export const requestOtp = (email: string) =>
  req<{ ok: true }>("POST", "/auth/request-otp", { email });
export const verifyOtp = (email: string, code: string) =>
  req<Me>("POST", "/auth/verify-otp", { email, code });
export const logout = () => req<{ ok: true }>("POST", "/auth/logout");
export const getMe = () => req<Me>("GET", "/me");

// ---- keys ----
export const getKeys = () => req<ApiKey[]>("GET", "/keys");
export const createKey = (name: string, domains: string[]) =>
  req<CreatedKey>("POST", "/keys", { name, domains });
export const rotateKey = (id: string) =>
  req<CreatedKey>("POST", `/keys/${id}/rotate`);
export const deleteKey = (id: string) => req<{ ok: true }>("DELETE", `/keys/${id}`);
export const addDomain = (id: string, domain: string) =>
  req<ApiKey>("POST", `/keys/${id}/domains`, { domain });
export const removeDomain = (id: string, domain: string) =>
  req<ApiKey>("DELETE", `/keys/${id}/domains`, { domain });

// ---- usage ----
export const getUsage = () => req<Usage>("GET", "/usage");

// ---- billing ----
export const createCheckout = (email: string, period: Period) =>
  req<{ url: string }>("POST", "/billing/checkout", { email, period });
export const openPortal = () => req<{ url: string }>("POST", "/billing/portal");

// ---- account ----
export const deleteAccount = () => req<{ ok: true }>("DELETE", "/account");

// ---- public demo ----
// Calls our same-origin, keyless demo endpoint (the Worker proxies to the box
// with the internal key server-side). Falls back to a local mock only if the
// endpoint is unreachable (e.g. offline).
export async function demoSearch(q: string): Promise<string> {
  try {
    const res = await fetch("/api/demo/search?" + new URLSearchParams({ q }));
    if (!res.ok) throw new Error(String(res.status));
    return JSON.stringify(await res.json(), null, 2);
  } catch {
    // Offline / not-yet-deployed fallback: deterministic mock so the box always shows something.
    return mockResult(q);
  }
}

function mockResult(q: string): string {
  const query = q.trim() || "unknown";
  let h = 0;
  for (const ch of query) h = (Math.imul(h, 31) + ch.charCodeAt(0)) >>> 0;
  const lat = ((h % 14000) / 100 - 70).toFixed(7);
  const lon = (((h >>> 3) % 36000) / 100 - 180).toFixed(7);
  return JSON.stringify(
    [
      {
        place_id: 100000000 + (h % 900000000),
        licence: "Data © OpenStreetMap contributors, ODbL 1.0",
        osm_type: "node",
        osm_id: 1000000000 + ((h >>> 1) % 900000000),
        lat,
        lon,
        class: "place",
        type: "house",
        importance: Number(((h % 80) / 100 + 0.12).toFixed(2)),
        display_name: query,
        boundingbox: [
          (Number(lat) - 0.004).toFixed(4),
          (Number(lat) + 0.004).toFixed(4),
          (Number(lon) - 0.004).toFixed(4),
          (Number(lon) + 0.004).toFixed(4),
        ],
      },
    ],
    null,
    2,
  );
}
